import React, { useState, useEffect, useRef } from "react";
import PostPreviewCardFull from "./PostPreviewCardFull";
import {
  fetchPostsPage,
  updatePostFields,
  generateReviewLink,
  revokeReviewLink,
  deletePost,
  POSTS_PAGE_SIZE,
} from "../../services/postService";
import { Post } from "../../types/postTypes";
import { ConnectedProfile } from "../../types/customer";
import { fetchCustomerById } from "../../services/customerService";
import { asConnectedProfileArray } from "../AIAgents/ChatWithAgent/utils/connectedProfiles";
import { useAuth } from "../../context/AuthContext";
import { useCustomerWorkspace } from "../../context/CustomerWorkspaceContext";
import PlatformFilter from "../Shared/PlatformFilter";
import StatusFilter from "../Shared/StatusFilter";
import GenerateReviewLink from "./GenerateReviewLink";
import ReviewLinksPanel from "./ReviewLinksPanel";
import ConfirmDialog from "../Shared/ConfirmDialog";
import { sendApprovalSignal } from "../../services/agentMemoryService";
import {
  buildScheduledPostUpdates,
  buildPlannedPostUpdates,
  getApiErrorMessage,
  publishAndFetchPlatformPostId,
} from "../../services/socialTokenService";
import { CognitoAuthService } from "../../services/cognitoService";
import { uploadMedia } from "../../features/media/utils/mediaUploadUtils";
import { useToast } from "../../context/ToastContext";
import { debug } from "../../utils/debugLogger";
import { MediaLibraryModal } from "../AIAgents/ChatWithAgent/components/MediaLibraryModal";
import { fetchMediaByCustomer } from "../../services/contentService";
import { MediaItem } from "../../types/MediaItem";
import { INSTAGRAM_CAROUSEL_MIN_PUBLISH_SLIDES } from "../AIAgents/ChatWithAgent/utils/instagramCarouselLimits";
import { instagramReviewPostHasPublishableMedia } from "../../utils/instagramPublishMediaValidation";
import {
  POST_MEDIA_REQUIRED_MESSAGE,
  platformRequiresMedia,
  postHasAnyMedia,
} from "../../utils/postMediaValidation";

import type { TikTokOptions } from "../../features/tiktok/types";
import {
  TIKTOK_INBOX_MESSAGE,
  TIKTOK_PROCESSING_MESSAGE,
  TIKTOK_SCHEDULE_INCOMPLETE_MESSAGE,
  isTikTokPlatform,
  missingTikTokRequiredFields,
  resolveTikTokProfileId,
  stampTikTokConsent,
  stripTikTokConsent,
} from "../../features/tiktok/utils/tiktokOptions";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

type InstagramLibraryFormat = "image" | "video" | "carousel";

const resolveImagePostType = (platform?: string) =>
  "SINGLE_IMAGE";

/** The posts API only accepts `carousel_media` on post_type CAROUSEL (2+ items), and rejects
 * a 1-item list on a single post. Persist [] below that; the UI keeps the slide locally. */
const carouselMediaForSave = <T,>(slides: T[]): T[] =>
  slides.length >= INSTAGRAM_CAROUSEL_MIN_PUBLISH_SLIDES ? slides : [];

const INSTAGRAM_IMAGE_REQUIRED_MESSAGE =
  "Add an image or video before approving or publishing on Instagram (Reels need a video).";

const isInstagramPost = (platform?: string) =>
  String(platform ?? "").toUpperCase().includes("INSTAGRAM");

const RedesignedReviewPost: React.FC = () => {
  const { selectedCustomerId } = useCustomerWorkspace();
  const { showToast } = useToast();
  // State declarations
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadingMediaPostId, setUploadingMediaPostId] = useState<
    string | null
  >(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [posts, setPosts] = useState<Post[]>([]);
  // Cursor pagination with Previous/Next (mirrors the media library):
  // pageTokens[i] is the start cursor for page i (page 0 = null).
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [pageTokens, setPageTokens] = useState<(string | null)[]>([null]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState<boolean>(false);
  const requestIdRef = useRef(0);
  const [editableTime, setEditableTime] = useState<Date | null>(null);
  const [editablePostId, setEditablePostId] = useState<string | null>(null);
  const [editableContent, setEditableContent] = useState<string>("");
  const [editableHashtags, setEditableHashtags] = useState<string>("");
  const [editableMediaPostId, setEditableMediaPostId] = useState<string | null>(
    null
  );
  const [selectedPosts, setSelectedPosts] = useState<
    Map<string, { customer_id: string; post_id: string }>
  >(new Map());
  const [editableCaptionPostId, setEditableCaptionPostId] = useState<
    string | null
  >(null);
  const [editableHashtagsPostId, setEditableHashtagsPostId] = useState<
    string | null
  >(null);
  const [publishingPostId, setPublishingPostId] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<{ [key: string]: string }>(
    {}
  );
  const [reviewLink, setReviewLink] = useState<string | null>(null);
  const [isGeneratingReview, setIsGeneratingReview] = useState(false);
  const { idToken } = useAuth();
  const [editableCallToActionPostId, setEditableCallToActionPostId] = useState<
    string | null
  >(null);
  const [editableCallToAction, setEditableCallToAction] = useState<string>("");
  const [editableBusinessProfileIdPostId, setEditableBusinessProfileIdPostId] =
    useState<string | null>(null);
  const [editableBusinessProfileId, setEditableBusinessProfileId] =
    useState<string>("");
  const [editableTitlePostId, setEditableTitlePostId] = useState<string | null>(
    null
  );
  const [editableTitle, setEditableTitle] = useState<string>("");
  const [pendingDelete, setPendingDelete] = useState<{
    postId: string;
    customerId: string;
  } | null>(null);
  const [isDeletingPost, setIsDeletingPost] = useState(false);
  // Publish/approve was attempted on a post with no media, on a platform where that is legal.
  // Holds the action until the user confirms it.
  const [pendingNoMediaAction, setPendingNoMediaAction] = useState<{
    postId: string;
    customerId: string;
    action: "publish" | "approve";
  } | null>(null);
  // SGL-501: approving a post whose planned time has already passed would hand the
  // scheduler a stale time, so ask the owner what they want instead.
  const [pastTimeDialog, setPastTimeDialog] = useState<{
    postId: string;
    customerId: string;
  } | null>(null);
  // Set when "Pick a new time" was chosen: saving a future time on that post also approves it.
  const approveOnSaveRef = useRef<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [approvingPostId, setApprovingPostId] = useState<string | null>(null);
  const [connectedProfiles, setConnectedProfiles] = useState<ConnectedProfile[]>([]);

  // Library & format states
  const [libraryContext, setLibraryContext] = useState<{
    postId: string;
    format: InstagramLibraryFormat;
  } | null>(null);
  const [libraryMediaItems, setLibraryMediaItems] = useState<MediaItem[]>([]);
  const [isLibraryLoading, setIsLibraryLoading] = useState(false);
  const [selectedLibraryItems, setSelectedLibraryItems] = useState<MediaItem[]>([]);

  // Fetch posts for selected customer
  useEffect(() => {
    if (selectedCustomerId) {
      const timer = setTimeout(() => {
        fetchPage1();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [selectedCustomerId, selectedPlatforms, selectedStatus]);

  // Connected profiles (needed for the LinkedIn Personal/Business Page toggle).
  useEffect(() => {
    if (!selectedCustomerId || !idToken) {
      setConnectedProfiles([]);
      return;
    }
    let cancelled = false;
    fetchCustomerById(selectedCustomerId, idToken)
      .then((customer) => {
        if (cancelled) return;
        setConnectedProfiles(asConnectedProfileArray(customer?.connected_profiles));
      })
      .catch((error) => {
        debug.error("Error loading connected profiles:", error);
        if (!cancelled) setConnectedProfiles([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCustomerId, idToken]);

  const handleSaveMedia = async (
    postId: string,
    mediaType: "image" | "video",
    directFile?: File
  ) => {
    const fileToUpload = directFile || selectedFile;
    if (!fileToUpload) {
      showToast("Please select a file to upload.", "warning");
      return;
    }

    try {
      // Don't use setLoading(true) here - it hides all posts!
      // setLoading(true);
      setUploadingMediaPostId(postId); // Start uploading state

      // Find the post to update
      const post = posts.find((p) => p.post_id === postId);
      if (!post) {
        throw new Error("Post not found");
      }

      // Use centralized upload utility - handles S3 upload + media registration
      const result = await uploadMedia({
        file: fileToUpload,
        customerId: post.customer_id,
        userEmail: CognitoAuthService.getUserEmail(idToken),
        token: idToken,
        isLogo: false,
        mediaType: "processed",
        additionalMetadata: {
          updated_from_post: true,
          post_id: postId,
        },
        tags: ["edited", `post:${postId}`],
      });

      if (!result.success) {
        throw new Error(result.errorMessage || "Failed to upload media");
      }

      const isIg = post.platform?.toUpperCase().includes("INSTAGRAM");
      // Match library single-media updates: stale carousel_media would otherwise win on publish
      // (applyInstagramCarouselToPublishPayload forces image_url from carousel when length ≥ 2).
      const updatePayload: Record<string, unknown> =
        mediaType === "image"
          ? {
              image_url: result.cloudFrontUrl,
              post_type: resolveImagePostType(post.platform),
              carousel_media: [],
              ...(isIg
                ? {
                    instagram_content_format: "image",
                    video_url: "",
                  }
                : {}),
            }
          : {
              video_url: result.cloudFrontUrl,
              post_type: "SINGLE_VIDEO",
              carousel_media: [],
              ...(isIg
                ? {
                    instagram_content_format: "video",
                    image_url: "",
                  }
                : {}),
            };

      await updatePostFields(postId, updatePayload, idToken, post.customer_id);

      // Update local state
      setPosts((prevPosts) =>
        prevPosts.map((p) =>
          p.post_id === postId ? { ...p, ...updatePayload } : p
        )
      );

      // Success and cleanup
      showToast("Media updated successfully.", "success");
      cancelEditMedia(postId);
    } catch (error) {
      debug.error(`Error updating ${mediaType}:`, error);
      showToast(`Failed to update ${mediaType}.`, "error");
    } finally {
      // Don't use setLoading(false) - we're not using the global loading state
      // setLoading(false);
      setUploadingMediaPostId(null); // Clear uploading state
    }
  };

  /**
   * Detach media from a post. Deliberately does NOT delete the S3 object or the Media
   * Library entry — the same CloudFront URL can be referenced by other posts, so removing
   * the file would break them. carousel_media is cleared for the same reason handleSaveMedia
   * clears it: a stale carousel would otherwise win over image_url when publishing. Safe to
   * send here because carousel posts render CarouselSlideStrip, never this component.
   */
  const handleRemoveMedia = async (
    postId: string,
    mediaType: "image" | "video"
  ) => {
    const post = posts.find((p) => p.post_id === postId);
    if (!post) {
      showToast("Post not found.", "error");
      return;
    }

    try {
      setUploadingMediaPostId(postId);

      const updatePayload: Record<string, unknown> =
        mediaType === "image"
          ? { image_url: "", carousel_media: [] }
          : { video_url: "", carousel_media: [] };

      await updatePostFields(postId, updatePayload, idToken, post.customer_id);

      setPosts((prevPosts) =>
        prevPosts.map((p) =>
          p.post_id === postId ? { ...p, ...updatePayload } : p
        )
      );

      // A post with no media cannot be published; clear any stale publish error so the
      // guard message the user sees next is the current one.
      setPublishError((prev) => ({ ...prev, [postId]: "" }));
      showToast(`${mediaType === "image" ? "Image" : "Video"} removed.`, "success");
    } catch (error) {
      debug.error(`Error removing ${mediaType}:`, error);
      showToast(`Failed to remove ${mediaType}.`, "error");
    } finally {
      setUploadingMediaPostId(null);
    }
  };

  // SGL-417: what the owner changes or rejects here teaches the Social Media Manager.
  // Only drafts awaiting approval, only after our own save succeeded, never blocking it.
  const [deleteReason, setDeleteReason] = useState<string>("");
  const isPendingPost = (post?: Post) => String(post?.status ?? "").toUpperCase() === "PENDING";
  const signalEdit = (post: Post | undefined, field: string, before?: string, after?: string) => {
    if (!post || !isPendingPost(post)) return;
    if ((before ?? "").trim() === (after ?? "").trim()) return;
    sendApprovalSignal(post.customer_id, { post_id: post.post_id, action: "edit", field, before, after });
  };

  const requestDeletePost = (postId: string, customerId: string) => {
    setDeleteReason("");
    setPendingDelete({ postId, customerId });
  };

  const confirmDeletePost = async () => {
    if (!pendingDelete) return;
    const { postId, customerId } = pendingDelete;
    const deleted = posts.find((p) => p.post_id === postId);
    setIsDeletingPost(true);
    try {
      await deletePost(customerId, postId, idToken);
      if (deleted && isPendingPost(deleted) && deleteReason.trim()) {
        sendApprovalSignal(customerId, {
          post_id: postId,
          action: "reject",
          before: deleted.content || "",
          reason: deleteReason.trim(),
        });
      }
      setDeleteReason("");
      setPosts((prevPosts) =>
        prevPosts.filter((post) => post.post_id !== postId)
      );
      setPendingDelete(null);
      showToast("Post deleted successfully.", "success");
    } catch (error) {
      debug.error("Error deleting post:", error);
      showToast("Failed to delete the post.", "error");
    } finally {
      setIsDeletingPost(false);
    }
  };

  const cancelDeletePost = () => {
    if (!isDeletingPost) setPendingDelete(null);
  };

  // The user accepted publishing/approving a post that has no media: re-run the original
  // action with the media check skipped.
  const confirmNoMediaAction = () => {
    const pending = pendingNoMediaAction;
    if (!pending) return;
    setPendingNoMediaAction(null);

    if (pending.action === "publish") {
      void handlePublishPost(pending.postId, pending.customerId, {
        skipMediaCheck: true,
      });
    } else {
      void handleApprove(pending.postId, { skipMediaCheck: true });
    }
  };

  // Dropping out of the time editor (Cancel) forgets the "approve on save" intent so a
  // later ordinary Save on the same post can't approve it by surprise.
  useEffect(() => {
    if (approveOnSaveRef.current && editablePostId !== approveOnSaveRef.current) {
      approveOnSaveRef.current = null;
    }
  }, [editablePostId]);

  const cancelPastTimeDialog = () => setPastTimeDialog(null);

  // "Publish now": approve, then run the normal publish for the post.
  const confirmPastTimePublishNow = () => {
    const pending = pastTimeDialog;
    if (!pending) return;
    setPastTimeDialog(null);
    void handleApprove(pending.postId, {
      skipMediaCheck: true,
      skipPastTimeCheck: true,
      publishNow: true,
    });
  };

  // "Pick a new time": open the card's time editor; the next valid save also approves.
  const pickNewTimeForPastPost = () => {
    const pending = pastTimeDialog;
    if (!pending) return;
    setPastTimeDialog(null);
    approveOnSaveRef.current = pending.postId;
    setEditableTime(null);
    setEditablePostId(pending.postId);
  };

  // Pagination is fully backend-driven via the next_token cursor, with
  // Previous/Next page navigation (same approach as the media library). Each page
  // replaces the list; pageTokens remembers the start cursor for each page so we
  // can step back. Server-side filters: platform + status (no date filter).

  // Fetch page 1 — resets pagination. Used on mount and on customer/filter change.
  const fetchPage1 = async () => {
    if (!selectedCustomerId) return;
    const reqId = ++requestIdRef.current;
    setLoading(true);
    setPosts([]);
    setNextToken(null);
    setPageTokens([null]);
    setCurrentPage(0);
    try {
      const { posts: fetchedPosts, nextToken: token } = await fetchPostsPage(
        selectedCustomerId,
        idToken,
        selectedPlatforms.length > 0 ? selectedPlatforms : undefined,
        selectedStatus || undefined,
        { limit: pageSize }
      );
      if (reqId !== requestIdRef.current) return; // stale
      setPosts(fetchedPosts);
      setNextToken(token);
      setPublishError({});
    } catch (error) {
      debug.error("Error fetching posts:", error);
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  };

  // Fetch a specific page given its start cursor; replaces the current list.
  const fetchPage = async (startToken: string | null, limit?: number) => {
    if (!selectedCustomerId) return;
    const reqId = requestIdRef.current;
    setLoading(true);
    try {
      const { posts: fetchedPosts, nextToken: token } = await fetchPostsPage(
        selectedCustomerId,
        idToken,
        selectedPlatforms.length > 0 ? selectedPlatforms : undefined,
        selectedStatus || undefined,
        { limit: limit ?? pageSize, nextToken: startToken }
      );
      if (reqId !== requestIdRef.current) return; // stale
      setPosts(fetchedPosts);
      setNextToken(token);
    } catch (error) {
      debug.error("Error fetching posts:", error);
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  };

  const handleNextPage = async () => {
    if (!nextToken) return;
    const newPage = currentPage + 1;
    setCurrentPage(newPage);
    setPageTokens((prev) => {
      const updated = [...prev];
      updated[newPage] = nextToken;
      return updated;
    });
    await fetchPage(nextToken);
  };

  const handlePrevPage = async () => {
    if (currentPage === 0) return;
    const newPage = currentPage - 1;
    setCurrentPage(newPage);
    await fetchPage(pageTokens[newPage] ?? null);
  };

  const handlePageSizeChange = async (newSize: number) => {
    setPageSize(newSize);
    setNextToken(null);
    setPageTokens([null]);
    setCurrentPage(0);
    setPosts([]);
    const reqId = ++requestIdRef.current;
    setLoading(true);
    try {
      const { posts: fetchedPosts, nextToken: token } = await fetchPostsPage(
        selectedCustomerId!,
        idToken,
        selectedPlatforms.length > 0 ? selectedPlatforms : undefined,
        selectedStatus || undefined,
        { limit: newSize }
      );
      if (reqId !== requestIdRef.current) return;
      setPosts(fetchedPosts);
      setNextToken(token);
    } catch (error) {
      debug.error("Error fetching posts:", error);
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  };

  // Selection handler
  const togglePostSelection = (postId: string, post?: { customer_id: string; post_id: string }) => {
    setSelectedPosts((prev) => {
      const next = new Map(prev);
      if (next.has(postId)) {
        next.delete(postId);
      } else if (post) {
        next.set(postId, post);
      }
      return next;
    });
  };

  // TikTok settings saves, serialized per post (see handleSaveTikTokOptions).
  const tiktokSaveChains = useRef<Map<string, Promise<void>>>(new Map());
  // Newest options per post, set synchronously so an action started right after an
  // edit doesn't read the pre-edit render's copy.
  const tiktokLatestOptions = useRef<Map<string, TikTokOptions>>(new Map());
  const tiktokSaveSeq = useRef<Map<string, number>>(new Map());
  const tiktokCommitted = useRef<Map<string, { options?: TikTokOptions; profileId?: string }>>(new Map());
  // Record options that a successful PUT just wrote (consent stamps).
  const publishInFlight = useRef<Set<string>>(new Set());
  const commitTikTokOptions = (postId: string, options: TikTokOptions, profileId?: string) => {
    tiktokLatestOptions.current.set(postId, options);
    const prev = tiktokCommitted.current.get(postId);
    tiktokCommitted.current.set(postId, { options, profileId: (profileId ?? prev?.profileId) as string });
  };
  const waitForTikTokSaves = (postId: string): Promise<void> =>
    tiktokSaveChains.current.get(postId) ?? Promise.resolve();
  const tiktokOptionsFor = (post: Post): TikTokOptions | undefined =>
    tiktokLatestOptions.current.get(post.post_id) ?? post.tiktok_options;
  // consent_by must never be empty: fall back to the token's sub when it has no email.
  const tiktokConsentUser = (): string =>
    CognitoAuthService.getUserEmail(idToken) || CognitoAuthService.getUserId(idToken) || "unknown-user";

  // Publish handler
  const handlePublishPost = async (
    postId: string,
    customerId: string,
    options?: { skipMediaCheck?: boolean; overrides?: Partial<Post> & { publish_type?: string } }
  ) => {
    const found = posts.find(
      (p) => p.post_id === postId && p.customer_id === customerId
    );
    const post = found && options?.overrides ? { ...found, ...options.overrides } : found;
    if (!post) {
      setPublishError((prev) => ({
        ...prev,
        [postId]: "Post not found for given post_id and customer_id",
      }));
      return;
    }
    if (!postHasAnyMedia(post)) {
      // Media-only platforms (Instagram, Reels, YouTube) would be rejected by the platform
      // API, so publishing without media can only fail: block it.
      if (platformRequiresMedia(post.platform)) {
        setPublishError((prev) => ({
          ...prev,
          [postId]: POST_MEDIA_REQUIRED_MESSAGE,
        }));
        showToast(POST_MEDIA_REQUIRED_MESSAGE, "warning");
        return;
      }
      // Elsewhere a text-only post is legal — most likely the image was removed by mistake,
      // so ask rather than decide for them.
      if (!options?.skipMediaCheck) {
        setPendingNoMediaAction({ postId, customerId, action: "publish" });
        return;
      }
    }
    if (isInstagramPost(post.platform) && !instagramReviewPostHasPublishableMedia(post)) {
      setPublishError((prev) => ({
        ...prev,
        [postId]: INSTAGRAM_IMAGE_REQUIRED_MESSAGE,
      }));
      showToast(INSTAGRAM_IMAGE_REQUIRED_MESSAGE, "warning");
      return;
    }

    // TikTok (SGL-170): the settings panel is the only place consent is captured.
    // Re-check it here, then record consent and the open_id on the row BEFORE the
    // publish call, because the backend rejects a TikTok row without them.
    const isTikTokPost = isTikTokPlatform(post.platform);
    let tiktokPost: Post = post;
    if (isTikTokPost) {
      // Lock the button before any await so a double click can't start a second publish.
      if (publishInFlight.current.has(postId)) return;
      publishInFlight.current.add(postId);
      setPublishingPostId(postId);
      setPublishError((prev) => ({ ...prev, [postId]: "" }));
      const release = () => {
        publishInFlight.current.delete(postId);
        setPublishingPostId(null);
      };
      try {
        // A settings save still in flight would land after the consent write and wipe it.
        await waitForTikTokSaves(postId);
        const tiktokOptions = tiktokOptionsFor(post);
        const tiktokProfileId = resolveTikTokProfileId(post.business_profile_id, connectedProfiles);
        const problems = tiktokProfileId
          ? missingTikTokRequiredFields(tiktokOptions, undefined, null, !!post.video_url)
          : ["No TikTok account is connected."];
        if (problems.length > 0) {
          setPublishError((prev) => ({ ...prev, [postId]: problems[0] }));
          showToast(problems[0], "warning");
          release();
          return;
        }
        const stamped = stampTikTokConsent(tiktokOptions, tiktokConsentUser());
        try {
          await updatePostFields(
            postId,
            { tiktok_options: stamped, business_profile_id: tiktokProfileId },
            idToken,
            customerId
          );
        } catch (error) {
          debug.error("Error saving TikTok consent:", error);
          setPublishError((prev) => ({
            ...prev,
            [postId]: "Could not save the TikTok settings. Please try again.",
          }));
          release();
          return;
        }
        commitTikTokOptions(postId, stamped, tiktokProfileId as string);
        tiktokPost = {
          ...post,
          tiktok_options: stamped,
          business_profile_id: tiktokProfileId as string,
        };
      } catch (error) {
        release();
        throw error;
      }
    } else {
      setPublishingPostId(postId);
      setPublishError((prev) => ({ ...prev, [postId]: "" }));
    }

    try {
      const platformUpper = String(post.platform || "").toUpperCase();
      const isYouTubePlatform =
        platformUpper === "YOUTUBE" || platformUpper === "YOUTUBE_SHORTS";
      if (isYouTubePlatform) {
        if (!post.privacy_status) {
          throw new Error(
            "YouTube publish requires Privacy to be set (public, unlisted, or private)."
          );
        }
        if (typeof post.made_for_kids !== "boolean") {
          throw new Error(
            "YouTube publish requires Audience to be set (Made for Kids or Not Made for Kids)."
          );
        }
      }
      const isMediaHeavyInstagramPost =
        post.platform?.toUpperCase().includes("INSTAGRAM") &&
        (post.post_type === "CAROUSEL" ||
          post.post_type === "SINGLE_VIDEO" ||
          Boolean(post.video_url));
      const isFacebookReelsPost =
        String(post.platform ?? "").toUpperCase() === "FACEBOOK_REELS";
      const isInstagramReelsPost =
        post.platform?.toUpperCase().includes("INSTAGRAM") &&
        post.platform?.toUpperCase().includes("REELS");
      const isLinkedInDocumentPost =
        String(post.platform ?? "").toUpperCase() === "LINKEDIN" &&
        Boolean((post as any).document_url || ((post as any).carousel_slides && (post as any).carousel_slides.length > 0));
      const isFacebookCarouselPost =
        String(post.platform ?? "").toUpperCase() === "FACEBOOK" &&
        post.post_type === "CAROUSEL";
      const result = await publishAndFetchPlatformPostId(tiktokPost, idToken, {
        maxRetries: isInstagramReelsPost
          ? 42
          : isMediaHeavyInstagramPost
            ? 28
            : isFacebookReelsPost
              ? 24
              : isFacebookCarouselPost
                ? 36
                : isLinkedInDocumentPost
                  ? 15
                  : 10,
        delayMs:
          isInstagramReelsPost ||
          isMediaHeavyInstagramPost ||
          isFacebookReelsPost ||
          isFacebookCarouselPost
            ? 5000
            : isLinkedInDocumentPost
              ? 5000
              : 10000,
      });
      if (result.error) {
        setPublishError((prev) => ({
          ...prev,
          [postId]: result.error || "Failed to publish.",
        }));
      } else if (isTikTokPost) {
        // The backend already wrote the terminal status; don't write it back.
        if (result.pending) {
          // Keep the stamped consent in local state and mark the row as in flight
          // (the scheduler's own ENQUEUED status) so Publish stays disabled.
          setPosts((prevPosts) =>
            prevPosts.map((p) =>
              p.post_id === postId ? { ...p, ...tiktokPost, status: "ENQUEUED" } : p
            )
          );
          showToast(result.message || TIKTOK_PROCESSING_MESSAGE, "info");
        } else {
          const sent = result.status === "SENT_TO_TIKTOK";
          setPosts((prevPosts) =>
            prevPosts.map((p) =>
              p.post_id === postId
                ? {
                    ...p,
                    ...tiktokPost,
                    status: sent ? "SENT_TO_TIKTOK" : "PUBLISHED",
                    updated_at: new Date().toISOString(),
                    platform_post_id: result.platform_post_id ?? "",
                    platform_response: result.platform_response,
                  }
                : p
            )
          );
          showToast(sent ? TIKTOK_INBOX_MESSAGE : `Published to TikTok. ${TIKTOK_PROCESSING_MESSAGE}`, "success");
        }
      } else {
        setPosts((prevPosts) =>
          prevPosts.map((post) =>
            post.post_id === postId
              ? {
                  ...post,
                  status: "PUBLISHED",
                  platform_post_id: result.platform_post_id,
                  business_profile_id: result.business_page_id,
                  platform_response: result.platform_response,
                }
              : post
          )
        );
        // Update the database with business_profile_id and platform_post_id
        try {
          await updatePostFields(
            postId,
            {
              status: "PUBLISHED",
              platform_post_id: result.platform_post_id,
              business_profile_id: result.business_page_id,
              platform_response: result.platform_response,
            },
            idToken,
            customerId
          );
        } catch (updateError) {
          debug.error("Failed to update post in database:", updateError);
          // Don't throw - the post was still published successfully
        }
        showToast(
          `Post published successfully${post.platform ? ` on ${post.platform}` : ""}.`,
          "success"
        );
      }
    } catch (error: any) {
      setPublishError((prev) => ({
        ...prev,
        [postId]: error?.message || "Failed to publish.",
      }));
    } finally {
      publishInFlight.current.delete(postId);
      setPublishingPostId(null);
    }
  };

  // Approve handler
  const handleApprove = async (
    postId: string,
    options?: {
      skipMediaCheck?: boolean;
      skipPastTimeCheck?: boolean;
      publishNow?: boolean;
    }
  ) => {
    try {
      setApprovingPostId(postId);
      const post = posts.find((p) => p.post_id === postId);
      if (!post) throw new Error("Post not found");
      if (!postHasAnyMedia(post)) {
        if (platformRequiresMedia(post.platform)) {
          showToast(POST_MEDIA_REQUIRED_MESSAGE, "warning");
          return;
        }
        if (!options?.skipMediaCheck) {
          setPendingNoMediaAction({
            postId,
            customerId: post.customer_id,
            action: "approve",
          });
          return;
        }
      }
      if (isInstagramPost(post.platform) && !instagramReviewPostHasPublishableMedia(post)) {
        showToast(INSTAGRAM_IMAGE_REQUIRED_MESSAGE, "warning");
        return;
      }
      // SGL-501: a planned time becomes the real schedule on approval. If it has already
      // passed, don't approve with a stale time; ask publish-now vs pick-a-new-time.
      if (
        !options?.skipPastTimeCheck &&
        String(post.status ?? "").toUpperCase() === "PENDING" &&
        post.scheduled_time?.trim()
      ) {
        const plannedMs = new Date(post.scheduled_time).getTime();
        if (!Number.isNaN(plannedMs) && plannedMs <= Date.now()) {
          setPastTimeDialog({ postId, customerId: post.customer_id });
          return;
        }
      }
      // TikTok (SGL-170): approving a post that already has a schedule is what lets the
      // scheduler pick it up, and the scheduler skips TikTok rows with no consent.
      // (Publish-now skips this: handlePublishPost validates and stamps consent itself.)
      let approveExtras: Record<string, unknown> = {};
      if (!options?.publishNow && isTikTokPlatform(post.platform) && post.scheduled_time?.trim()) {
        await waitForTikTokSaves(postId);
        const tiktokOptions = tiktokOptionsFor(post);
        const tiktokProfileId = resolveTikTokProfileId(post.business_profile_id, connectedProfiles);
        const problems = tiktokProfileId
          ? missingTikTokRequiredFields(tiktokOptions, undefined, null, !!post.video_url)
          : ["No TikTok account is connected."];
        if (problems.length > 0) {
          showToast(`Set the TikTok settings before approving a post with a planned time. ${problems[0]}`, "warning");
          return;
        }
        approveExtras = {
          tiktok_options: stampTikTokConsent(tiktokOptions, tiktokConsentUser()),
          business_profile_id: tiktokProfileId,
        };
      }
      await updatePostFields(
        postId,
        {
          status: "APPROVED",
          // Publish now: clear the stale planned time so the scheduler (which scans
          // APPROVED rows whose scheduled_time is due) can't also enqueue this post.
          ...(options?.publishNow
            ? { publish_type: "immediate", scheduled_time: "" }
            : {}),
          ...approveExtras,
        },
        idToken,
        post.customer_id
      );
      if (approveExtras.tiktok_options) {
        commitTikTokOptions(postId, approveExtras.tiktok_options as TikTokOptions, approveExtras.business_profile_id as string);
      }
      setPosts((prevPosts) =>
        prevPosts.map((post) =>
          post.post_id === postId
            ? {
                ...post,
                status: "APPROVED",
                ...(options?.publishNow ? { scheduled_time: "" } : {}),
                ...approveExtras,
              }
            : post
        )
      );
      showToast("Post status updated to APPROVED.", "success");
      if (options?.publishNow) {
        // handlePublishPost does not write APPROVED itself (it only writes the
        // terminal PUBLISHED status), so the approval above is not repeated.
        // `posts` in handlePublishPost is this render's stale closure (still PENDING with
        // the old time), so hand it the fresh values for the payload it publishes.
        await handlePublishPost(postId, post.customer_id, {
          skipMediaCheck: true,
          overrides: { status: "APPROVED", publish_type: "immediate", scheduled_time: "" },
        });
      }
    } catch (error) {
      debug.error("Error updating post status:", error);
      showToast("Failed to update post status.", "error");
    } finally {
      setApprovingPostId(null);
    }
  };

  // Save time handler
  const handleSaveTime = async (
    postId: string,
    newTime: Date | null,
    timezone?: string
  ) => {
    try {
      if (!newTime) return;
      const post = posts.find((p) => p.post_id === postId);
      if (!post) throw new Error("Post not found");

      // SGL-501: a PENDING post only gets a *planned* time (status untouched; the
      // scheduler ignores it until the owner approves). APPROVED keeps the real
      // schedule behaviour. "Pick a new time" on a past-planned post (approveOnSave)
      // writes the real schedule and approves in the same save.
      const statusUpper = String(post.status ?? "").toUpperCase();
      const isPending = statusUpper === "PENDING";
      if (statusUpper !== "APPROVED" && !isPending) {
        showToast("Only pending or approved posts can have their time changed.", "warning");
        return;
      }
      const approveOnSave = isPending && approveOnSaveRef.current === postId;
      if (approveOnSave && newTime.getTime() <= Date.now()) {
        showToast("Pick a time in the future to approve this post.", "warning");
        return;
      }
      const plannedOnly = isPending && !approveOnSave;

      // TikTok (SGL-170): scheduling records consent to the chosen settings, so they
      // must be valid first; stamp consent with the schedule (the scheduler skips
      // a TikTok row that has none).
      let tiktokScheduleUpdates: Record<string, unknown> = {};
      if (!plannedOnly && isTikTokPlatform(post.platform)) {
        await waitForTikTokSaves(postId);
        const tiktokOptions = tiktokOptionsFor(post);
        const tiktokProfileId = resolveTikTokProfileId(post.business_profile_id, connectedProfiles);
        const problems = tiktokProfileId
          ? missingTikTokRequiredFields(tiktokOptions, undefined, null, !!post.video_url)
          : ["No TikTok account is connected."];
        if (problems.length > 0) {
          showToast(problems[0], "warning");
          return;
        }
        tiktokScheduleUpdates = {
          tiktok_options: stampTikTokConsent(tiktokOptions, tiktokConsentUser()),
          business_profile_id: tiktokProfileId,
        };
      }

      setIsSaving(true);
      const formattedTime = newTime.toISOString();
      // Pass the picked zone through. It used to be dropped here (the card has
      // always sent it, and CreatePostComponentV2 forwards it), which stored an
      // empty scheduled_timezone and pushed the backend onto its
      // SCHEDULER_DEFAULT_TIMEZONE fallback instead of the user's choice.
      const zone = timezone || post.scheduled_timezone;
      const updates = plannedOnly
        ? buildPlannedPostUpdates(formattedTime, zone)
        : {
            ...buildScheduledPostUpdates(formattedTime, zone),
            ...tiktokScheduleUpdates,
          };
      await updatePostFields(
        postId,
        updates,
        idToken,
        post.customer_id
      );
      if (tiktokScheduleUpdates.tiktok_options) {
        commitTikTokOptions(postId, tiktokScheduleUpdates.tiktok_options as TikTokOptions, tiktokScheduleUpdates.business_profile_id as string);
      }
      setPosts((prevPosts) =>
        prevPosts.map((post) =>
          post.post_id === postId
            ? { ...post, ...updates }
            : post
        )
      );
      approveOnSaveRef.current = null;
      setEditablePostId(null);
      showToast(
        plannedOnly
          ? "Planned time saved · goes out once approved."
          : approveOnSave
          ? "Post approved and scheduled."
          : "Schedule updated successfully.",
        "success"
      );
    } catch (error) {
      debug.error("Error updating post schedule:", error);
      showToast("Failed to update schedule.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // Save hashtags handler
  const handleSaveHashtags = async (postId: string) => {
    try {
      setIsSaving(true);
      const post = posts.find((p) => p.post_id === postId);
      if (!post) throw new Error("Post not found");
      await updatePostFields(
        postId,
        { hashtags: editableHashtags },
        idToken,
        post.customer_id
      );
      signalEdit(post, "hashtags", post.hashtags, editableHashtags);
      setPosts((prevPosts) =>
        prevPosts.map((post) =>
          post.post_id === postId
            ? { ...post, hashtags: editableHashtags || "" }
            : post
        )
      );
      setEditableHashtagsPostId(null);
      setEditableHashtags("");
      showToast("Hashtags updated successfully.", "success");
    } catch (error) {
      debug.error("Error updating hashtags:", error);
      showToast("Failed to update hashtags.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // Save caption handler
  const handleSaveCaption = async (postId: string) => {
    try {
      setIsSaving(true);
      const post = posts.find((p) => p.post_id === postId);
      if (!post) throw new Error("Post not found");
      await updatePostFields(
        postId,
        { content: editableContent },
        idToken,
        post.customer_id
      );
      signalEdit(post, "caption", post.content, editableContent);
      setPosts((prevPosts) =>
        prevPosts.map((post) =>
          post.post_id === postId
            ? { ...post, content: editableContent || "" }
            : post
        )
      );
      setEditableCaptionPostId(null);
      setEditableContent("");
      showToast("Caption updated successfully.", "success");
    } catch (error) {
      debug.error("Error updating caption:", error);
      showToast("Failed to update caption.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // Media edit handlers
  const enableEditMedia = (postId: string) => {
    setEditableMediaPostId(postId);
  };
  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    postId: string
  ) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };
  const cancelEditMedia = (postId: string) => {
    setEditableMediaPostId(null);
    setSelectedFile(null);
  };

  const handleSaveCallToAction = async (postId: string) => {
    try {
      setIsSaving(true);
      const post = posts.find((p) => p.post_id === postId);
      if (!post) throw new Error("Post not found");
      await updatePostFields(
        postId,
        { call_to_action: editableCallToAction },
        idToken,
        post.customer_id
      );
      signalEdit(post, "call_to_action", post.call_to_action, editableCallToAction);
      setPosts((prevPosts) =>
        prevPosts.map((post) =>
          post.post_id === postId
            ? { ...post, call_to_action: editableCallToAction || "" }
            : post
        )
      );
      setEditableCallToActionPostId(null);
      setEditableCallToAction("");
      showToast("Call to action updated successfully.", "success");
    } catch (error) {
      debug.error("Error updating call to action:", error);
      showToast("Failed to update call to action.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // Save business profile ID handler. `overrideValue` lets the LinkedIn
  // Personal/Business Page toggle save its resolved URN directly, without
  // going through the editableBusinessProfileId text-edit state.
  const handleSaveBusinessProfileId = async (postId: string, overrideValue?: string) => {
    const valueToSave = overrideValue ?? editableBusinessProfileId;
    try {
      setIsSaving(true);
      const post = posts.find((p) => p.post_id === postId);
      if (!post) throw new Error("Post not found");
      await updatePostFields(
        postId,
        { business_profile_id: valueToSave },
        idToken,
        post.customer_id
      );
      setPosts((prevPosts) =>
        prevPosts.map((post) =>
          post.post_id === postId
            ? { ...post, business_profile_id: valueToSave || "" }
            : post
        )
      );
      setEditableBusinessProfileIdPostId(null);
      setEditableBusinessProfileId("");
      showToast("Business profile updated successfully.", "success");
    } catch (error) {
      debug.error("Error updating business profile ID:", error);
      showToast("Failed to update business profile.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // Save title handler
  const handleSaveTitle = async (postId: string, customerId: string) => {
    try {
      setIsSaving(true);
      await updatePostFields(
        postId,
        { title: editableTitle },
        idToken,
        customerId
      );
      signalEdit(posts.find((p) => p.post_id === postId), "title",
        posts.find((p) => p.post_id === postId)?.title, editableTitle);
      setPosts((prevPosts) =>
        prevPosts.map((post) =>
          post.post_id === postId
            ? { ...post, title: editableTitle || "" }
            : post
        )
      );
      setEditableTitlePostId(null);
      setEditableTitle("");
      showToast("Title updated successfully.", "success");
    } catch (error) {
      debug.error("Error updating title:", error);
      showToast("Failed to update title.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // Save TikTok Direct Post settings (SGL-170). Also pins business_profile_id to the
  // connected open_id: the publisher rejects the "default_*" placeholder.
  //
  // Consent is never carried over: the stored consent covered the settings as they
  // were, and the backend rejects a half-edited object that still has consent_at.
  // A scheduled + approved post gets consent re-stamped in the same PUT when the new
  // settings are valid; otherwise it saves without consent and the user is told it
  // won't go out. Saves run one after another per post, and publish / schedule /
  // approve wait for them (waitForTikTokSaves), so a stale save can't land after a
  // consent write and wipe it.
  const handleSaveTikTokOptions = (postId: string, options: TikTokOptions): Promise<void> => {
    const post = posts.find((p) => p.post_id === postId);
    if (!post) return Promise.resolve();
    const profileId = resolveTikTokProfileId(post.business_profile_id, connectedProfiles);
    let nextOptions = stripTikTokConsent(options);
    const updates: Record<string, unknown> = {};
    if (profileId && profileId !== post.business_profile_id) {
      updates.business_profile_id = profileId;
    }
    const scheduledAndApproved =
      String(post.status ?? "").toUpperCase() === "APPROVED" && !!post.scheduled_time?.trim();
    let wontGoOut = false;
    if (scheduledAndApproved) {
      const problems = profileId
        ? missingTikTokRequiredFields(nextOptions, undefined, null, !!post.video_url)
        : ["No TikTok account is connected."];
      if (problems.length === 0) {
        nextOptions = stampTikTokConsent(nextOptions, tiktokConsentUser());
        updates.business_profile_id = profileId;
      } else {
        wontGoOut = true;
      }
    }
    updates.tiktok_options = nextOptions;
    // Last state known to be in the DB; a failed save rolls back to this, not to the
    // snapshot at queue time (which may include a still-pending earlier save).
    if (!tiktokCommitted.current.has(postId)) {
      tiktokCommitted.current.set(postId, {
        options: tiktokLatestOptions.current.get(postId) ?? post.tiktok_options,
        profileId: post.business_profile_id,
      });
    }
    const seq = (tiktokSaveSeq.current.get(postId) ?? 0) + 1;
    tiktokSaveSeq.current.set(postId, seq);
    tiktokLatestOptions.current.set(postId, nextOptions);
    setPosts((prev) => prev.map((p) => (p.post_id === postId ? { ...p, ...updates } : p)));

    const run = async () => {
      const isNewest = () => tiktokSaveSeq.current.get(postId) === seq;
      try {
        await updatePostFields(postId, updates, idToken, post.customer_id);
        tiktokCommitted.current.set(postId, {
          options: nextOptions,
          profileId: (updates.business_profile_id as string | undefined) ?? post.business_profile_id,
        });
        // Saves are serialized, so the last success is what the DB holds: re-apply it
        // in case an earlier failed save rolled the UI back over it.
        if (isNewest()) {
          tiktokLatestOptions.current.set(postId, nextOptions);
          setPosts((prev) => prev.map((p) => (p.post_id === postId ? { ...p, ...updates } : p)));
        }
        if (wontGoOut) showToast(TIKTOK_SCHEDULE_INCOMPLETE_MESSAGE, "warning");
      } catch (error) {
        debug.error("Error saving TikTok options:", error);
        // A newer save is queued: it owns the final state, don't clobber it.
        if (isNewest()) {
          const committed = tiktokCommitted.current.get(postId);
          if (committed?.options) tiktokLatestOptions.current.set(postId, committed.options);
          else tiktokLatestOptions.current.delete(postId);
          setPosts((prev) =>
            prev.map((p) =>
              p.post_id === postId
                ? { ...p, tiktok_options: committed?.options, business_profile_id: committed?.profileId as string }
                : p
            )
          );
        }
        showToast("Failed to save the TikTok settings.", "error");
      }
    };
    const chained = (tiktokSaveChains.current.get(postId) ?? Promise.resolve()).then(run);
    tiktokSaveChains.current.set(postId, chained);
    return chained;
  };

  // Save privacy status handler (for YouTube)
  const handleSavePrivacy = async (postId: string, privacyStatus: string) => {
    try {
      setIsSaving(true);
      const post = posts.find((p) => p.post_id === postId);
      if (!post) throw new Error("Post not found");

      await updatePostFields(
        postId,
        { privacy_status: privacyStatus },
        idToken,
        post.customer_id
      );

      setPosts((prevPosts) =>
        prevPosts.map((post) =>
          post.post_id === postId
            ? { ...post, privacy_status: privacyStatus }
            : post
        )
      );
    } catch (error) {
      debug.error("Error updating privacy status:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // Save made for kids handler (for YouTube COPPA compliance)
  const handleSaveMadeForKids = async (
    postId: string,
    madeForKids: boolean
  ) => {
    try {
      setIsSaving(true);
      const post = posts.find((p) => p.post_id === postId);
      if (!post) throw new Error("Post not found");

      await updatePostFields(
        postId,
        { made_for_kids: madeForKids },
        idToken,
        post.customer_id
      );

      setPosts((prevPosts) =>
        prevPosts.map((post) =>
          post.post_id === postId
            ? { ...post, made_for_kids: madeForKids }
            : post
        )
      );
    } catch (error) {
      debug.error("Error updating made for kids status:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // ── Carousel slide handlers ──────────────────────────────
  const handleAddCarouselSlides = async (postId: string, files: FileList | File[]) => {
    const post = posts.find((p) => p.post_id === postId);
    if (!post) return;
    const isFacebookPost = post.platform?.toUpperCase() === "FACEBOOK";
    try {
      setUploadingMediaPostId(postId);
      const newSlides: { url: string; type: string; order: number }[] = [];
      const existing = post.carousel_media ?? [];
      let baseOrder = existing.length;
      let skippedVideoCount = 0;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const isVideo = file.type.startsWith("video/");
        if (isVideo && isFacebookPost) {
          skippedVideoCount += 1;
          continue;
        }
        const result = await uploadMedia({
          file: file,
          customerId: post.customer_id,
          userEmail: CognitoAuthService.getUserEmail(idToken),
          token: idToken,
          isLogo: false,
          mediaType: "processed",
          additionalMetadata: {
            updated_from_post: true,
            post_id: postId,
          },
          tags: ["edited", `post:${postId}`, "carousel_slide"],
        });

        if (!result.success) {
          throw new Error(result.errorMessage || "Failed to upload media");
        }
        newSlides.push({
          url: result.cloudFrontUrl,
          type: isVideo ? "VIDEO" : "IMAGE",
          order: baseOrder++,
        });
      }

      if (newSlides.length === 0) {
        showToast(
          "Facebook multi-photo posts are image-only. Pick photos instead of videos.",
          "error"
        );
        return;
      }

      const next = [...existing, ...newSlides];
      const updates: Record<string, unknown> = {
        carousel_media: carouselMediaForSave(next),
        image_url: next[0]?.url ?? post.image_url ?? "",
      };
      if (next.length >= INSTAGRAM_CAROUSEL_MIN_PUBLISH_SLIDES) {
        updates.post_type = "CAROUSEL";
      } else {
        updates.post_type = resolveImagePostType(post.platform);
      }
      if (post.platform?.toUpperCase().includes("INSTAGRAM")) {
        updates.instagram_content_format = "carousel";
      }

      await updatePostFields(postId, updates, idToken, post.customer_id);
      setPosts((prev) =>
        prev.map((p) =>
          p.post_id === postId ? { ...p, ...updates, carousel_media: next } : p
        )
      );
      if (skippedVideoCount > 0) {
        showToast(
          `Added ${newSlides.length} photo(s). Skipped ${skippedVideoCount} video(s) — Facebook multi-photo posts are image-only.`,
          "success"
        );
      } else {
        showToast("Carousel slides added successfully.", "success");
      }
    } catch (error) {
      debug.error("Error adding carousel slides:", error);
      showToast("Failed to add carousel slides.", "error");
    } finally {
      setUploadingMediaPostId(null);
    }
  };

  const handleRemoveCarouselSlide = async (postId: string, index: number) => {
    const post = posts.find((p) => p.post_id === postId);
    if (!post?.carousel_media?.length) return;
    try {
      setIsSaving(true);
      const next = post.carousel_media.filter((_, i) => i !== index)
        .map((s, i) => ({ ...s, order: i }));
      const updates: Record<string, unknown> = {
        carousel_media: carouselMediaForSave(next),
        image_url: next.length ? (next[0]?.url ?? "") : "",
      };
      if (next.length >= INSTAGRAM_CAROUSEL_MIN_PUBLISH_SLIDES) {
        updates.post_type = "CAROUSEL";
      } else {
        updates.post_type = resolveImagePostType(post.platform);
      }
      if (post.platform?.toUpperCase().includes("INSTAGRAM")) {
        updates.instagram_content_format = "carousel";
      }
      await updatePostFields(postId, updates, idToken, post.customer_id);
      setPosts((prev) =>
        prev.map((p) =>
          p.post_id === postId ? { ...p, ...updates, carousel_media: next } : p
        )
      );
      showToast("Slide removed.", "success");
    } catch (error) {
      debug.error("Error removing carousel slide:", error);
      showToast("Failed to remove slide.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleReorderCarouselSlides = async (
    postId: string,
    fromIndex: number,
    toIndex: number
  ) => {
    const post = posts.find((p) => p.post_id === postId);
    if (!post?.carousel_media) return;
    try {
      const next = [...post.carousel_media];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      const ordered = next.map((s, i) => ({ ...s, order: i }));
      // Optimistic local update
      setPosts((prev) =>
        prev.map((p) =>
          p.post_id === postId
            ? {
                ...p,
                carousel_media: ordered,
                image_url: ordered[0]?.url ?? "",
              }
            : p
        )
      );
      await updatePostFields(
        postId,
        {
          carousel_media: ordered,
          image_url: ordered[0]?.url ?? "",
        },
        idToken,
        post.customer_id
      );
    } catch (error) {
      debug.error("Error reordering carousel slides:", error);
      showToast("Error adding items to carousel.", "error");
    }
  };

  const handleOpenLibrary = async (
    postId: string,
    format: InstagramLibraryFormat
  ) => {
    setLibraryContext({ postId, format });
    setSelectedLibraryItems([]);
    if (!selectedCustomerId || !idToken) return;
    setIsLibraryLoading(true);
    try {
      const isFacebookPost =
        posts.find((p) => p.post_id === postId)?.platform?.toUpperCase() === "FACEBOOK";
      const resolvedType: "image" | "video" | undefined =
        format === "image" || (format === "carousel" && isFacebookPost)
          ? "image"
          : format === "video"
            ? "video"
            : undefined;
      const { medias } = await fetchMediaByCustomer(selectedCustomerId, idToken, {
        limit: 50,
        mediaType: resolvedType,
      });
      setLibraryMediaItems(medias);
    } catch (err) {
      debug.error("Error fetching library media:", err);
      showToast("Could not load Media Library.", "error");
    } finally {
      setIsLibraryLoading(false);
    }
  };

  const handleLibraryDone = async (pickedItem?: MediaItem) => {
    const context = libraryContext;
    setLibraryContext(null);
    const { postId, format } = context || {};
    if (!postId || !format) return;
    const selectedItems =
      pickedItem && format !== "carousel"
        ? [pickedItem]
        : selectedLibraryItems;
    if (selectedItems.length === 0) return;

    const post = posts.find((p) => p.post_id === postId);
    if (!post) return;

    let updates: Record<string, unknown> = {};
    let localCarousel: typeof post.carousel_media | undefined;
    let successMessage = "Media updated.";

    if (format === "image") {
      const selectedItem = selectedItems[0];
      if (!selectedItem) return;
      updates = {
        image_url: selectedItem.cloudfront_url,
        post_type: resolveImagePostType(post.platform),
      };
      if (post.platform?.toUpperCase().includes("INSTAGRAM")) {
        updates.instagram_content_format = "image";
        updates.carousel_media = [];
        updates.video_url = "";
      }
      successMessage = "Image selected from library.";
    } else if (format === "video") {
      const selectedItem = selectedItems[0];
      if (!selectedItem) return;
      updates = {
        video_url: selectedItem.cloudfront_url,
        post_type: "SINGLE_VIDEO",
      };
      if (post.platform?.toUpperCase().includes("INSTAGRAM")) {
        updates.instagram_content_format = "video";
        updates.carousel_media = [];
        updates.image_url = "";
      }
      successMessage = "Video selected from library.";
    } else {
      // Facebook multi-photo posts are image-only; the library list is already
      // filtered, but never trust it alone.
      const isFacebookPost = post.platform?.toUpperCase() === "FACEBOOK";
      const usableItems = isFacebookPost
        ? selectedItems.filter((item) => item.media_type.toLowerCase() !== "video")
        : selectedItems;
      if (usableItems.length === 0) {
        showToast("Facebook multi-photo posts are image-only. Pick photos instead of videos.", "error");
        return;
      }
      const newSlides = usableItems.map((item, i) => ({
        url: item.cloudfront_url,
        type: item.media_type.toLowerCase() === "video" ? "VIDEO" : "IMAGE",
        order: (post.carousel_media?.length || 0) + i,
      }));
      const next = [...(post.carousel_media || []), ...newSlides].slice(0, 10);
      localCarousel = next as typeof post.carousel_media;
      updates = {
        carousel_media: carouselMediaForSave(next),
        image_url: next[0]?.url ?? post.image_url ?? "",
      };
      if (next.length >= INSTAGRAM_CAROUSEL_MIN_PUBLISH_SLIDES) {
        updates.post_type = "CAROUSEL";
      } else {
        updates.post_type = resolveImagePostType(post.platform);
      }
      if (post.platform?.toUpperCase().includes("INSTAGRAM")) {
        updates.instagram_content_format = "carousel";
        updates.video_url = "";
      }
      successMessage = `Added ${newSlides.length} items to carousel.`;
    }

    try {
      await updatePostFields(postId, updates, idToken, post.customer_id);
      setPosts((prev) =>
        prev.map((p) =>
          p.post_id === postId
            ? {
                ...p,
                ...updates,
                ...(localCarousel ? { carousel_media: localCarousel } : {}),
              }
            : p
        )
      );
      showToast(successMessage, "success");
    } catch (err) {
      showToast(getApiErrorMessage(err, "Error selecting media from library."), "error");
    }
  };

  const handleFormatChange = async (
    postId: string,
    newFormat: "image" | "video" | "carousel"
  ): Promise<boolean> => {
    const post = posts.find((p) => p.post_id === postId);
    if (!post) return false;
    const isIg = post.platform?.toUpperCase().includes("INSTAGRAM");
    const isFb = post.platform?.toUpperCase() === "FACEBOOK";
    let updates: Record<string, unknown>;
    let localCarousel: typeof post.carousel_media | undefined;
    if (newFormat === "carousel") {
      let initialCarousel = [...(post.carousel_media || [])];
      if (initialCarousel.length === 0 && post.image_url) {
        initialCarousel.push({ url: post.image_url, type: "IMAGE" as const, order: 0 });
      }
      initialCarousel = initialCarousel.map((s, i) => ({ ...s, order: i }));
      const heroUrl = initialCarousel[0]?.url || post.image_url || "";
      const post_type =
        initialCarousel.length >= INSTAGRAM_CAROUSEL_MIN_PUBLISH_SLIDES
          ? "CAROUSEL"
          : resolveImagePostType(post.platform);
      localCarousel = initialCarousel;
      updates = {
        post_type,
        carousel_media: carouselMediaForSave(initialCarousel),
        image_url: heroUrl,
        video_url: "",
        ...(isIg ? { instagram_content_format: "carousel" as const } : {}),
      };
    } else if (newFormat === "image") {
      const heroUrl = post.carousel_media?.[0]?.url || post.image_url || "";
      updates = {
        post_type: resolveImagePostType(post.platform),
        image_url: heroUrl,
        video_url: post.video_url || "",
        carousel_media: isIg || isFb ? [] : post.carousel_media || [],
        ...(isIg ? { instagram_content_format: "image" as const } : {}),
      };
    } else {
      updates = {
        post_type: "SINGLE_VIDEO",
        // Facebook keeps its image_url, so never reuse it as the video source.
        video_url: post.video_url || (isFb ? "" : post.image_url || ""),
        image_url: isIg ? "" : post.image_url || "",
        carousel_media: isIg || isFb ? [] : post.carousel_media || [],
        ...(isIg ? { instagram_content_format: "video" as const } : {}),
      };
    }
    try {
      await updatePostFields(postId, updates, idToken, post.customer_id);
      setPosts((prev) =>
        prev.map((p) =>
          p.post_id === postId
            ? { ...p, ...updates, ...(localCarousel ? { carousel_media: localCarousel } : {}) }
            : p
        )
      );
      showToast(`Post format changed to ${newFormat}.`, "success");
      return true;
    } catch (err) {
      showToast(getApiErrorMessage(err, "Failed to change format."), "error");
      return false;
    }
  };

  // Render
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-theme-fg">Review Posts</h1>
      {/* CustomerDropdown removed per design change */}
      <div className="mb-6 rounded-lg border border-theme-border bg-theme-background p-4 shadow-sm">
        <h2 className="mb-3 text-lg font-semibold text-theme-fg">Filter Posts</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <PlatformFilter
            selectedPlatforms={selectedPlatforms}
            onChange={setSelectedPlatforms}
          />
          <StatusFilter
            selectedStatus={selectedStatus}
            onChange={setSelectedStatus}
          />
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-theme-fg">Items per page</label>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="rounded border border-theme-border bg-surface-ui px-3 py-2 text-sm text-theme-fg"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {loading && <p className="text-theme-fg-muted">Loading reviews...</p>}
      <GenerateReviewLink
        selectedPosts={Array.from(selectedPosts.values())}
        onGenerate={async (postsForReview) =>
          await generateReviewLink(postsForReview, idToken)
        }
        onRevoke={async (customerId, linkId) => {
          await revokeReviewLink(customerId, linkId, idToken);
        }}
        onClear={() => setSelectedPosts(new Map())}
      />
      <ReviewLinksPanel
        customerId={selectedCustomerId}
        idToken={idToken}
        posts={posts}
      />
      {!loading && selectedCustomerId ? (
        posts.length > 0 ? (
          <div className="space-y-4">
            {posts.map((post, index) => (
              <PostPreviewCardFull
                key={index}
                post={post}
                isSelected={selectedPosts.has(post.post_id)}
                onSelect={togglePostSelection}
                editablePostId={editablePostId}
                setEditablePostId={setEditablePostId}
                editableTime={editableTime}
                setEditableTime={setEditableTime}
                handleSaveTime={handleSaveTime}
                editableCaptionPostId={editableCaptionPostId}
                setEditableCaptionPostId={setEditableCaptionPostId}
                editableContent={editableContent}
                setEditableContent={setEditableContent}
                handleSaveCaption={handleSaveCaption}
                editableHashtagsPostId={editableHashtagsPostId}
                setEditableHashtagsPostId={setEditableHashtagsPostId}
                editableHashtags={editableHashtags}
                setEditableHashtags={setEditableHashtags}
                handleSaveHashtags={handleSaveHashtags}
                editableMediaPostId={editableMediaPostId}
                enableEditMedia={enableEditMedia}
                handleFileChange={handleFileChange}
                handleSaveMedia={handleSaveMedia}
                handleRemoveMedia={handleRemoveMedia}
                cancelEditMedia={cancelEditMedia}
                selectedFile={selectedFile}
                handleApprove={handleApprove}
                plannedTimeForPending
                handlePublishPost={handlePublishPost}
                publishingPostId={publishingPostId}
                publishError={publishError}
                editableCallToActionPostId={editableCallToActionPostId}
                setEditableCallToActionPostId={setEditableCallToActionPostId}
                editableCallToAction={editableCallToAction}
                setEditableCallToAction={setEditableCallToAction}
                handleSaveCallToAction={handleSaveCallToAction}
                editableBusinessProfileIdPostId={
                  editableBusinessProfileIdPostId
                }
                setEditableBusinessProfileIdPostId={
                  setEditableBusinessProfileIdPostId
                }
                editableBusinessProfileId={editableBusinessProfileId}
                setEditableBusinessProfileId={setEditableBusinessProfileId}
                handleSaveBusinessProfileId={handleSaveBusinessProfileId}
                connectedProfiles={connectedProfiles}
                editableTitlePostId={editableTitlePostId}
                setEditableTitlePostId={setEditableTitlePostId}
                editableTitle={editableTitle}
                setEditableTitle={setEditableTitle}
                handleSaveTitle={handleSaveTitle}
                handleDeletePost={requestDeletePost}
                uploadingMediaPostId={uploadingMediaPostId}
                handleSavePrivacy={handleSavePrivacy}
                handleSaveMadeForKids={handleSaveMadeForKids}
                handleSaveTikTokOptions={handleSaveTikTokOptions}
                tiktokProfileId={resolveTikTokProfileId(post.business_profile_id, connectedProfiles)}
                idToken={idToken}
                isSaving={isSaving}
                approvingPostId={approvingPostId}
                handleRemoveCarouselSlide={handleRemoveCarouselSlide}
                handleReorderCarouselSlides={handleReorderCarouselSlides}
                handleAddCarouselSlides={handleAddCarouselSlides}
                handleFormatChange={handleFormatChange}
                handleOpenLibrary={handleOpenLibrary}
              />
            ))}
          </div>
        ) : (
          <p className="text-theme-fg-muted">
            No posts found for the selected company.
          </p>
        )
      ) : !selectedCustomerId ? (
        <p className="text-theme-fg-muted">Select a company to view posts.</p>
      ) : null}
      {!loading && selectedCustomerId && (currentPage > 0 || nextToken) && (
        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            type="button"
            disabled={currentPage === 0 || loading}
            onClick={handlePrevPage}
            className="inline-flex items-center gap-1 rounded-md border border-theme-border bg-surface-ui px-4 py-2 text-sm font-medium text-theme-fg transition-colors hover:bg-surface-ui-hover disabled:opacity-40"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </button>
          <span className="text-sm text-theme-fg-muted">Page {currentPage + 1}</span>
          <button
            type="button"
            disabled={!nextToken || loading}
            onClick={handleNextPage}
            className="inline-flex items-center gap-1 rounded-md bg-theme-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:opacity-90 disabled:opacity-40"
          >
            Next
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}
      <ConfirmDialog
        isOpen={pendingNoMediaAction !== null}
        title="Post without an image?"
        message={
          <p>
            This post has no image or video attached. It will go out as text only.
            Add media first if that wasn&apos;t intended.
          </p>
        }
        confirmLabel={
          pendingNoMediaAction?.action === "approve"
            ? "Approve anyway"
            : "Publish anyway"
        }
        cancelLabel="Cancel"
        onConfirm={confirmNoMediaAction}
        onCancel={() => setPendingNoMediaAction(null)}
      />
      <ConfirmDialog
        isOpen={pastTimeDialog !== null}
        title="This post's planned time has passed"
        message={
          <div className="space-y-3">
            <p>
              The time planned for this post is already in the past. Publish it
              now, or pick a new time and it will be approved and scheduled.
            </p>
            <button
              type="button"
              className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
              onClick={pickNewTimeForPastPost}
            >
              Pick a new time
            </button>
          </div>
        }
        confirmLabel="Publish now"
        cancelLabel="Cancel"
        onConfirm={confirmPastTimePublishNow}
        onCancel={cancelPastTimeDialog}
      />
      <ConfirmDialog
        isOpen={pendingDelete !== null}
        title="Delete post?"
        message={
          <>
          <p>
            Deleting this post only removes it from this system. If you have
            posted on your social channel, you&apos;ll need to delete there
            separately.
          </p>
          {isPendingPost(posts.find((p) => p.post_id === pendingDelete?.postId)) && (
            <label className="block mt-3">
              <span className="block text-sm font-medium mb-1">
                Why are you rejecting it? (optional)
              </span>
              <textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                maxLength={500}
                rows={2}
                placeholder="e.g. too salesy, too long, wrong tone"
                className="w-full p-2 border border-gray-300 rounded text-gray-800"
              />
              <span className="block text-xs text-gray-500 mt-1">
                Your Social Media Manager remembers this for future drafts.
              </span>
            </label>
          )}
          </>
        }
        confirmLabel="Proceed"
        cancelLabel="Cancel"
        isConfirming={isDeletingPost}
        onConfirm={confirmDeletePost}
        onCancel={cancelDeletePost}
      />

      <MediaLibraryModal
        isOpen={!!libraryContext}
        onClose={() => setLibraryContext(null)}
        mediaItems={libraryMediaItems}
        selectedImages={selectedLibraryItems}
        onImageSelect={(item: MediaItem) => {
          if (libraryContext?.format === "image" || libraryContext?.format === "video") {
            setSelectedLibraryItems([item]);
            return;
          }
          setSelectedLibraryItems((prev) => {
            if (prev.some((p) => p.media_id === item.media_id)) {
              return prev.filter((p) => p.media_id !== item.media_id);
            }
            if (prev.length >= 10) return prev;
            return [...prev, item];
          });
        }}
        loading={isLibraryLoading}
        multiSelectMode={libraryContext?.format === "carousel"}
        onPressDone={handleLibraryDone}
        maxSlots={libraryContext?.format === "carousel" ? 10 : 1}
        mediaTypeFilter={
          libraryContext?.format === "image"
            ? "image"
            : libraryContext?.format === "video"
              ? "video"
              : "all"
        }
      />
    </div>
  );
};

export default RedesignedReviewPost;
